import React, { useState, useEffect } from 'react';
import { workflowApi } from '../services/api';

interface WorkflowExecutionSummary {
  workflowType: string;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  runningExecutions: number;
  successRate: number;
  averageDuration: number;
  recentExecutions: ExecutionSummary[];
  errorSummary: ErrorSummary[];
}

interface ExecutionSummary {
  executionArn: string;
  name: string;
  status: 'SUCCEEDED' | 'FAILED' | 'RUNNING' | 'ABORTED' | 'TIMED_OUT';
  startDate: string;
  stopDate?: string;
  duration?: number;
  hasErrors: boolean;
}

interface ErrorSummary {
  errorType: string;
  count: number;
  lastOccurrence: string;
  affectedSteps: string[];
}

interface LogEvent {
  timestamp: string;
  message: string;
  logGroupName: string;
  logStreamName: string;
  level: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
}

interface ObservabilityDashboardProps {
  selectedWorkflowType?: string;
}

const ObservabilityDashboard: React.FC<ObservabilityDashboardProps> = ({ selectedWorkflowType }) => {
  const [workflowType, setWorkflowType] = useState(selectedWorkflowType || 'data-processing');
  const [timeRange, setTimeRange] = useState('24h');
  const [summary, setSummary] = useState<WorkflowExecutionSummary | null>(null);
  const [selectedExecution, setSelectedExecution] = useState<ExecutionSummary | null>(null);
  const [errorLogs, setErrorLogs] = useState<LogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Update workflowType when selectedWorkflowType prop changes
  useEffect(() => {
    if (selectedWorkflowType) {
      setWorkflowType(selectedWorkflowType);
    }
  }, [selectedWorkflowType]);

  useEffect(() => {
    loadExecutionSummary();
  }, [workflowType, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadExecutionSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await workflowApi.getExecutionSummary(workflowType, timeRange);
      setSummary(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load execution summary');
    } finally {
      setLoading(false);
    }
  };

  const loadErrorLogs = async (executionArn: string) => {
    try {
      const result = await workflowApi.getExecutionErrorLogs(executionArn);
      setErrorLogs(result.data.errorLogs || []);
    } catch (err) {
      console.error('Failed to load error logs:', err);
      setErrorLogs([]);
    }
  };

  const handleExecutionClick = async (execution: ExecutionSummary) => {
    setSelectedExecution(execution);
    if (execution.status === 'FAILED' || execution.hasErrors) {
      setShowErrorDetails(true);
      await loadErrorLogs(execution.executionArn);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCEEDED': return 'text-green-600 bg-green-100';
      case 'FAILED': return 'text-red-600 bg-red-100';
      case 'RUNNING': return 'text-blue-600 bg-blue-100';
      case 'ABORTED': return 'text-gray-600 bg-gray-100';
      case 'TIMED_OUT': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading observability data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <button
              onClick={loadExecutionSummary}
              className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return <div>No data available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workflow Observability</h2>
          <p className="text-gray-600">Monitor workflow executions, success rates, and error details</p>
        </div>
        
        <div className="flex space-x-4">
          <select
            value={workflowType}
            onChange={(e) => setWorkflowType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="data-processing">Data Processing</option>
            <option value="image-processing">Image Processing</option>
          </select>
          
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          
          <button
            onClick={loadExecutionSummary}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">📊</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Executions</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.totalExecutions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Successful</p>
              <p className="text-2xl font-semibold text-green-600">{summary.successfulExecutions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">❌</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Failed</p>
              <p className="text-2xl font-semibold text-red-600">{summary.failedExecutions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🔄</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Running</p>
              <p className="text-2xl font-semibold text-blue-600">{summary.runningExecutions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">📈</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Success Rate</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.successRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Executions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Executions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Execution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary.recentExecutions.map((execution) => (
                <tr key={execution.executionArn} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{execution.name}</div>
                    <div className="text-sm text-gray-500">{execution.executionArn.split(':').pop()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(execution.status)}`}>
                      {execution.status}
                    </span>
                    {execution.hasErrors && (
                      <span className="ml-2 text-xs text-red-600">⚠️ Has Errors</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(execution.startDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(execution.duration)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleExecutionClick(execution)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View Details
                    </button>
                    {(execution.status === 'FAILED' || execution.hasErrors) && (
                      <button
                        onClick={() => handleExecutionClick(execution)}
                        className="ml-3 text-red-600 hover:text-red-900"
                      >
                        View Errors
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Summary */}
      {summary.errorSummary.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Error Summary</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.errorSummary.map((error) => (
                <div key={error.errorType} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-red-800">{error.errorType}</h4>
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                      {error.count} occurrences
                    </span>
                  </div>
                  <div className="text-sm text-red-600 space-y-1">
                    <div>Last: {formatDate(error.lastOccurrence)}</div>
                    <div>Affected steps: {error.affectedSteps.join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Error Details Modal */}
      {showErrorDetails && selectedExecution && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Error Details - {selectedExecution.name}
              </h3>
              <button
                onClick={() => setShowErrorDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Status:</strong> {selectedExecution.status}</div>
                <div><strong>Start:</strong> {formatDate(selectedExecution.startDate)}</div>
                <div><strong>Duration:</strong> {formatDuration(selectedExecution.duration)}</div>
                <div><strong>ARN:</strong> {selectedExecution.executionArn}</div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              <h4 className="font-medium text-gray-900 mb-2">Error Logs</h4>
              {errorLogs.length > 0 ? (
                <div className="space-y-2">
                  {errorLogs.map((log, index) => (
                    <div key={index} className="border-l-4 border-red-400 bg-red-50 p-3">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs text-red-600 font-medium">{log.level}</span>
                        <span className="text-xs text-gray-500">{formatDate(log.timestamp)}</span>
                      </div>
                      <div className="text-sm text-red-800 font-mono whitespace-pre-wrap">
                        {log.message}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {log.logGroupName} / {log.logStreamName}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">
                  No error logs found for this execution
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObservabilityDashboard;
